import { type CompanySummary, formatDate } from "@pcj/shared";
import { Building2, ChevronRight } from "lucide-react";
import { Link, Navigate } from "react-router";
import { AppHeader } from "../../components/AppHeader";
import { Banner, Card, EmptyState, PageLoader, Pill, Table, td, th } from "../../components/ui";
import { errorMessage } from "../../lib/api";
import { plural } from "../../lib/format";
import { useChamberCompanies } from "../../lib/queries";
import { homePath, useCurrentUser } from "../SessionPages";

function Stat({ label, value, tone }: { label: string; value: number; tone?: "red" | "amber" }) {
  const color = tone === "red" ? "text-rose-600" : tone === "amber" ? "text-amber-600" : "text-gray-900";
  return (
    <Card className="p-4">
      <p className={`text-2xl font-semibold ${color}`}>{value}</p>
      <p className="mt-1 text-xs uppercase tracking-wide text-gray-500">{label}</p>
    </Card>
  );
}

const total = (rows: CompanySummary[], key: keyof CompanySummary) =>
  rows.reduce((sum, row) => sum + (row[key] as number), 0);

/** Read-only overview of every affiliated jewelry store for the chamber admin. */
export function ChamberPage() {
  const me = useCurrentUser();
  const companies = useChamberCompanies();
  if (me.profile.role !== "chamber_admin") return <Navigate to={homePath(me)} replace />;

  return (
    <div className="min-h-screen">
      <AppHeader me={me} title="Panel de la Cámara · todas las joyerías" />
      <main className="mx-auto max-w-6xl space-y-5 px-4 py-6 sm:px-8">
        {companies.isPending && <PageLoader label="Cargando joyerías…" />}
        {companies.isError && <Banner tone="error">{errorMessage(companies.error)}</Banner>}
        {companies.data && companies.data.length === 0 && (
          <EmptyState icon={Building2} title="Todavía ningún socio se ha registrado">
            Comparte el enlace del portal con las joyerías: cada una crea su cuenta y registra su empresa.
          </EmptyState>
        )}
        {companies.data && companies.data.length > 0 && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Joyerías" value={companies.data.length} />
              <Stat label="Alertas activas" value={total(companies.data, "activeAlerts")} tone="amber" />
              <Stat label="Avisos vencidos" value={total(companies.data, "overdueNotices")} tone="red" />
              <Stat
                label="Informes en cero pendientes"
                value={total(companies.data, "pendingZeroReports")}
                tone="amber"
              />
            </div>
            <Table>
              <thead className="bg-gray-50">
                <tr>
                  <th className={th}>Joyería</th>
                  <th className={th}>Clientes</th>
                  <th className={th}>Operaciones</th>
                  <th className={th}>Alertas</th>
                  <th className={th}>Avisos pendientes</th>
                  <th className={th}>Informes en cero</th>
                  <th className={th} aria-label="Ver" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {companies.data.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50">
                    <td className={td}>
                      <Link to={`/companies/${company.id}`} className="font-medium text-gray-900 hover:underline">
                        {company.legalName}
                      </Link>
                      <p className="text-xs text-gray-500">
                        {company.complianceOfficer ?? "Sin responsable registrado"} · RFC {company.rfc ?? "—"} · alta{" "}
                        {formatDate(company.createdAt.slice(0, 10))}
                      </p>
                    </td>
                    <td className={`${td} text-gray-600`}>{company.clients}</td>
                    <td className={`${td} text-gray-600`}>{company.operations}</td>
                    <td className={td}>
                      {company.activeAlerts > 0 ? (
                        <Pill tone="amber">{company.activeAlerts}</Pill>
                      ) : (
                        <Pill tone="green">0</Pill>
                      )}
                    </td>
                    <td className={td}>
                      {company.pendingNotices === 0 ? (
                        <Pill tone="green">Al corriente</Pill>
                      ) : (
                        <Pill tone={company.overdueNotices > 0 ? "red" : "amber"}>
                          {company.pendingNotices}
                          {company.overdueNotices > 0 && ` · ${plural(company.overdueNotices, "vencido", "vencidos")}`}
                        </Pill>
                      )}
                    </td>
                    <td className={td}>
                      {company.pendingZeroReports > 0 ? (
                        <Pill tone="amber">{plural(company.pendingZeroReports, "pendiente", "pendientes")}</Pill>
                      ) : (
                        <Pill tone="green">Al corriente</Pill>
                      )}
                    </td>
                    <td className={`${td} text-right`}>
                      <Link
                        to={`/companies/${company.id}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-gold-700 hover:underline"
                      >
                        Ver expediente <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </main>
    </div>
  );
}
